/**
 * ReviewStep — the Create Workshop wizard summary step, plus its row builder.
 *
 * Extracted from workshopCreateSteps so each file declares one component
 * (`react/no-multi-comp`); behaviour is unchanged.
 */
import type { WorkshopCreateForm, WorkshopDraft } from './useWorkshopCreateForm';
import type { SeriesPreview } from './workshopCreateSeries';
import { WorkshopDateWarning } from './WorkshopDateWarning';

const COL: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 14 };

interface ReviewLabels {
  typeLabel: string;
  regionLabel: string;
  venueLabel: string;
  /** The series the schedule produces, when it repeats. */
  series: SeriesPreview | null;
}

/** The Review summary rows (extracted to keep ReviewStep under the complexity
 *  cap). */
function reviewRows(
  draft: WorkshopDraft,
  labels: ReviewLabels,
): ReadonlyArray<readonly [string, string]> {
  const { workshop } = draft;
  return [
    ['Region', labels.regionLabel || '—'],
    ['Venue', labels.venueLabel || '—'],
    ...scheduleRows(draft.schedule, labels.series),
    ['Type', labels.typeLabel || '—'],
    ['Capacity', workshop.capacity || '—'],
    ['Ticket price', workshop.price ? `$${workshop.price}` : '—'],
  ];
}

/** " (2 dates changed)" when the operator moved any series dates by hand. */
function editedNote(count: number): string {
  if (count === 0) return '';
  return count === 1 ? ' (1 date changed)' : ` (${count} dates changed)`;
}

/** The date row, plus the cadence row when the schedule repeats. */
function scheduleRows(
  schedule: WorkshopDraft['schedule'],
  series: SeriesPreview | null,
): ReadonlyArray<readonly [string, string]> {
  const timeRange =
    schedule.date && schedule.start && schedule.end
      ? `${schedule.date} · ${schedule.start}–${schedule.end}`
      : '—';
  if (!series) return [['Date', timeRange]];
  return [
    ['First date', timeRange],
    ['Repeats', `${series.label} · ${series.count} workshops${editedNote(series.editedCount)}`],
  ];
}

/** What the CTA will create, said plainly at the point of no return. */
function createsNote(series: SeriesPreview | null): string {
  if (series) {
    return `Creates ${series.count} draft workshops. Publish each from its workshop page once you’re ready to open bookings.`;
  }
  return 'Creates the workshop in draft. Publish from the workshop page once you’re ready to open bookings.';
}

export function ReviewStep({
  form,
  typeLabel,
  regionLabel,
  venueLabel,
}: {
  form: WorkshopCreateForm;
  typeLabel: string;
  regionLabel: string;
  venueLabel: string;
}) {
  const { draft, submitError, dateWarning } = form;
  const series = form.seriesPreview?.problem === null ? form.seriesPreview : null;
  const rows = reviewRows(draft, { typeLabel, regionLabel, venueLabel, series });
  return (
    <div style={COL}>
      <div style={{ border: '1px solid var(--nb-border)', borderRadius: 10, overflow: 'hidden' }}>
        {rows.map(([k, v], i) => (
          <div
            key={k}
            style={{
              display: 'flex',
              gap: 10,
              padding: '8px 12px',
              borderBottom: i < rows.length - 1 ? '1px solid var(--nb-border)' : 'none',
              fontSize: 11.5,
            }}
          >
            <span style={{ width: 90, color: 'var(--nb-subtle)', fontWeight: 700 }}>{k}</span>
            <span style={{ color: 'var(--nb-ink)' }}>{v}</span>
          </div>
        ))}
      </div>
      {/* Repeated here so the lead time is seen at the point of no return —
          the operator may have set the date several steps ago (BON-508). */}
      <WorkshopDateWarning message={dateWarning} heading="CREATE ANYWAY?" />
      <div style={{ fontSize: 10.5, color: 'var(--nb-subtle)', lineHeight: 1.5 }}>
        {createsNote(series)}
      </div>
      {submitError && <div style={{ fontSize: 11.5, color: '#c0392b' }}>{submitError}</div>}
    </div>
  );
}
